import BrandLogo from "@/components/BrandLogo";
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, Users, PanelLeft, Calendar, Shield, LineChart, Settings2, Trophy, Hammer, CreditCard, Rocket, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import NotificationsDialog from "./NotificationsDialog";
import DevToolbar from "./DevToolbar";
import EditProfileDialog from "./EditProfileDialog";
import { supabasePostsService } from "@/services/supabasePostsService";
import { Capacitor } from '@capacitor/core';
interface HeaderProps {
  currentPath: string;
}
const Header = ({
  currentPath
}: HeaderProps) => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
  const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
  const [editProfileDefaultTab, setEditProfileDefaultTab] = useState<string>('profile');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const notificationBtnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { canAccessAdminPages } = usePermissions();
  const { logout, user } = useAuth();
  const { effectiveUserId, isImpersonating } = useImpersonation();

  // Load notification count on mount and when user changes
  useEffect(() => {
    const loadNotificationCount = async () => {
      if (!effectiveUserId) {
        setNotificationCount(0);
        return;
      }
      try {
        const count = await supabasePostsService.getUnreadNotificationsCount(effectiveUserId);
        setNotificationCount(count);
      } catch (error) {
        console.error('Error loading notification count:', error);
      }
    };
    loadNotificationCount();
  }, [effectiveUserId]);

  const handleNotificationCountChange = (count: number) => {
    setNotificationCount(count);
  };

  const handleNotificationClick = () => {
    setNotificationsDialogOpen(true);
  };
  const handleEditProfileClick = () => {
    setEditProfileDefaultTab('profile');
    setEditProfileDialogOpen(true);
  };
  const handleNotificationsSettingsClick = () => {
    setEditProfileDefaultTab('notifications');
    setEditProfileDialogOpen(true);
  };
  const handleAuthenticationClick = () => {
    setEditProfileDefaultTab('authentication');
    setEditProfileDialogOpen(true);
  };
  const handleLogout = async () => {
    await logout();
  };

  const handleMobileSearchToggle = () => {
    setMobileSearchOpen(!mobileSearchOpen);
  };

  const handleMobileSearchClose = () => {
    setMobileSearchOpen(false);
  };

  // Update popover state
  const handleOpenChange = (open: boolean) => {
    setPopoverOpen(open);
  };

  // Menu items from the sidebar that we're moving to the header dropdown
  const menuItems = [{
    path: "/admin/audience-management",
    icon: Users,
    label: "קהל"
  }, {
    path: "/admin/posts-management",
    icon: PanelLeft,
    label: "פוסטים"
  }, {
    path: "/moderation",
    icon: Shield,
    label: "מודרציה"
  }, {
    path: "/analytics",
    icon: LineChart,
    label: "אנליטיקה"
  }, {
    path: "/admin/onboarding-management",
    icon: Settings2,
    label: "ניהול אונבורדינג"
  }, {
    path: "/admin/funnel-management",
    icon: Rocket,
    label: "ניהול משפך רכישה"
  }, {
    path: "/admin/course-products",
    icon: ShoppingBag,
    label: "קורסים למכירה"
  }, {
    path: "/admin/challenge-management",
    icon: Trophy,
    label: "ניהול אתגרים וקורסים"
  }, {
    path: "/admin/fasting-notifications",
    icon: Settings2,
    label: "נוטיפקציות צומות"
  }, {
    path: "/admin/notifications",
    icon: Bell,
    label: "ניהול התראות"
  }, {
    path: "/admin/subscription-banners",
    icon: CreditCard,
    label: "ניהול באנרים"
  }, {
    path: "/admin/entry-popups",
    icon: Hammer,
    label: "פופ-אפים בכניסה"
  }];
  const isGuest = !user;

  return <>
    <header className="sticky top-0 z-50 bg-muted-warm/95 pt-[env(safe-area-inset-top)]">
      <div className="container mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-6 space-x-reverse">
          <div className="flex items-center gap-4">
            {!isGuest && canAccessAdminPages() && !isImpersonating && !Capacitor.isNativePlatform() && !isMobile && <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                  <div className="flex items-center cursor-pointer">
                    <BrandLogo variant="header" className="h-8" alt="לוגו" />
                    <ChevronDown className={`h-4 w-4 me-1 transition-transform ${popoverOpen ? 'rotate-180' : ''}`} />
                  </div>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" sideOffset={5} className="w-64 p-0 bg-card border shadow-lg animate-in fade-in-50 data-[side=bottom]:slide-in-from-top-5 app-dropdown">
                  <div className="p-4 relative">
                    <div className="space-y-1">
                      {menuItems.map(item => {
                    const isActive = currentPath === item.path;
                    return <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${isActive ? 'bg-primary text-primary-foreground shadow-sm font-semibold' : 'text-muted-foreground'}`} onClick={() => handleOpenChange(false)}>
                            <item.icon className={`h-5 w-5 ms-1 ${isActive ? 'text-primary-foreground' : ''}`} />
                            <span>{item.label}</span>
                            {isActive}
                          </Link>;
                  })}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>}
            {(isGuest || !canAccessAdminPages() || isImpersonating || Capacitor.isNativePlatform() || isMobile) && <Link to={isGuest ? "/library" : "/"}><BrandLogo variant="header" className="h-8" alt="לוגו" /></Link>}
            {currentPath === '/'}
          </div>

        </div>

        <div className="flex items-center space-x-3 space-x-reverse">
          {isGuest ? (
            <Button variant="default" size="sm" className="rounded-xl tap-target" onClick={() => navigate('/login')}>
              התחברות
            </Button>
          ) : (
            <>
              {canAccessAdminPages() && !isImpersonating && (
                <Button variant="ghost" size="icon" className={`relative ${devToolsOpen ? 'bg-primary/20 text-primary' : ''}`} onClick={() => setDevToolsOpen(!devToolsOpen)} aria-label="כלי פיתוח">
                  <Hammer className="h-5 w-5" />
                </Button>
              )}

              <Button variant="ghost" size="icon" aria-label="אירועים" className={`relative ${currentPath === '/events' ? 'bg-primary/20 text-primary' : ''}`} onClick={() => {
              if (currentPath === '/events') {
                navigate(-1);
              } else {
                navigate('/events');
              }
            }}>
                <Calendar className="h-5 w-5" />
              </Button>

              <Button
                ref={notificationBtnRef}
                variant="ghost"
                size="icon"
                className="relative"
                onClick={handleNotificationClick}
                aria-label={
                  notificationCount === 0 ? "התראות"
                    : notificationCount === 1 ? "התראות, אחת חדשה"
                    : `התראות, ${notificationCount} חדשות`
                }
              >
                <Bell className="h-5 w-5" />
                {/* Capped like the bottom nav's badge: a fixed 20px circle can't hold "142". */}
                {notificationCount > 0 && <span aria-hidden="true" className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full text-xs min-w-5 h-5 px-1 tabular-nums flex items-center justify-center">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>}
              </Button>
            </>
          )}
        </div>
      </div>

      <NotificationsDialog open={notificationsDialogOpen} onOpenChange={setNotificationsDialogOpen} triggerRef={notificationBtnRef} onCountChange={handleNotificationCountChange} />
      <EditProfileDialog open={editProfileDialogOpen} onOpenChange={setEditProfileDialogOpen} defaultTab={editProfileDefaultTab} />
    </header>

    <DevToolbar open={devToolsOpen} onOpenChange={setDevToolsOpen} />
  </>;
};
export default Header;